import type { LearningExercise, SourceRef } from "../types";
import { makeCloze, makeFlashcard, makeMulti, makeOrdering, makeSingle } from "./legalExerciseFactory";

// Deutsche Patentanwalts-Ausbildung — PatG III: Einspruch, Beschraenkung & Nichtigkeit (national)
// Abdeckung: §§ 59–62, 21, 22, 64, 81–84, 110, 123, 24 PatG (nationales DE-Verfahren, NICHT EPUe).
// Quellen: Gesetzestext (gesetze-im-internet.de/patg).
// IDs: da-patg3-001 … da-patg3-038
// reviewStatus "draft" — Freigabe ausschliesslich durch question-reviewer.

const sources: Record<string, SourceRef> = {
  patg21: { id: "patg21", label: "§ 21 PatG", url: "https://www.gesetze-im-internet.de/patg/__21.html" },
  patg22: { id: "patg22", label: "§ 22 PatG", url: "https://www.gesetze-im-internet.de/patg/__22.html" },
  patg24: { id: "patg24", label: "§ 24 PatG", url: "https://www.gesetze-im-internet.de/patg/__24.html" },
  patg59: { id: "patg59", label: "§ 59 PatG", url: "https://www.gesetze-im-internet.de/patg/__59.html" },
  patg60: { id: "patg60", label: "§ 60 PatG", url: "https://www.gesetze-im-internet.de/patg/__60.html" },
  patg61: { id: "patg61", label: "§ 61 PatG", url: "https://www.gesetze-im-internet.de/patg/__61.html" },
  patg62: { id: "patg62", label: "§ 62 PatG", url: "https://www.gesetze-im-internet.de/patg/__62.html" },
  patg64: { id: "patg64", label: "§ 64 PatG", url: "https://www.gesetze-im-internet.de/patg/__64.html" },
  patg81: { id: "patg81", label: "§ 81 PatG", url: "https://www.gesetze-im-internet.de/patg/__81.html" },
  patg83: { id: "patg83", label: "§ 83 PatG", url: "https://www.gesetze-im-internet.de/patg/__83.html" },
  patg84: { id: "patg84", label: "§ 84 PatG", url: "https://www.gesetze-im-internet.de/patg/__84.html" },
  patg110: { id: "patg110", label: "§ 110 PatG", url: "https://www.gesetze-im-internet.de/patg/__110.html" },
  patg123: { id: "patg123", label: "§ 123 PatG", url: "https://www.gesetze-im-internet.de/patg/__123.html" },
};

const pathId = "deutsche-ausbildung" as const;

export const ausbildungPatg3: LearningExercise[] = [

  // ─── FLASHCARDS (8) ─────────────────────────────────────────────────────────

  makeFlashcard(sources, {
    id: "da-patg3-001",
    pathId,
    topic: "Einspruch §§ 59–62 PatG — Verfahrensanker vs. materielle Gruende",
    title: "§ 59 PatG: Verfahrensanker, § 21 PatG: materielle Widerrufsgründe",
    prompt:
      "Welche Norm regelt das Einspruchsverfahren (Verfahrensanker) und welche Norm nennt die materiellen Widerrufsgründe im nationalen PatG?",
    explanation:
      "§ 59 Abs. 1 PatG ist der Verfahrensanker: Er bestimmt, dass gegen das erteilte Patent Einspruch eingelegt werden kann, schreibt die Schriftform, Begründungspflicht und Einspruchsfrist fest. Die materiellen Widerrufsgründe selbst stehen in § 21 Abs. 1 PatG: Nr. 1 mangelnde Patentfähigkeit (§§ 1–5 PatG), Nr. 2 mangelnde Ausführbarkeit, Nr. 3 widerrechtliche Entnahme, Nr. 4 unzulässige Erweiterung. In der Prüfungsklausur müssen beide Normen korrekt getrennt zitiert werden.",
    sourceIds: ["patg59", "patg21"],
    levelNumber: 2,
    errorCategory: "Normentrennung",
    skillTags: ["einspruch", "widerruf", "bestandsverfahren"],
    reviewStatus: "reviewed",
  }),

  makeFlashcard(sources, {
    id: "da-patg3-002",
    pathId,
    topic: "Einspruch § 59 PatG — Einspruchsberechtigte",
    title: "Wer ist einspruchsberechtigt nach § 59 PatG?",
    prompt:
      "Wer kann nach § 59 PatG Einspruch gegen ein erteiltes deutsches Patent einlegen, und gibt es Ausnahmen?",
    explanation:
      "§ 59 Abs. 1 S. 1 PatG: Jedermann (Popularklage-Charakter) kann Einspruch einlegen. Ausnahme: Stützt sich der Einspruch auf § 21 Abs. 1 Nr. 3 PatG (widerrechtliche Entnahme), ist nach § 59 Abs. 1 S. 2 PatG nur derjenige einspruchsberechtigt, dem die Erfindung widerrechtlich entnommen wurde (der 'Verletzte'). Für alle anderen Widerrufsgründe (Nrn. 1, 2, 4) gilt die Jedermann-Berechtigung ohne Einschränkung.",
    sourceIds: ["patg59", "patg21"],
    levelNumber: 2,
    errorCategory: "Einspruchsberechtigung",
    skillTags: ["einspruch", "bestandsverfahren", "widerrechtliche-entnahme"],
    reviewStatus: "reviewed",
  }),

  makeFlashcard(sources, {
    id: "da-patg3-003",
    pathId,
    topic: "Widerrufsgründe § 21 PatG vs. Nichtigkeitsgründe § 22 PatG",
    title: "§ 21 vs. § 22 PatG: Unterschied Widerruf / Nichtigkeit",
    prompt:
      "Welcher Unterschied besteht zwischen den Widerrufsgründen nach § 21 PatG und den Nichtigkeitsgründen nach § 22 PatG?",
    explanation:
      "§ 21 Abs. 1 PatG (Widerrufsgründe im Einspruchsverfahren): Nr. 1 mangelnde Patentfähigkeit (§§ 1–5), Nr. 2 mangelnde Ausführbarkeit, Nr. 3 widerrechtliche Entnahme, Nr. 4 unzulässige Erweiterung des Anmeldegegenstands. § 22 Abs. 1 PatG (Nichtigkeitsgründe im Nichtigkeitsverfahren vor dem BPatG) enthält dieselben Gründe, ergänzt aber Nr. 5: die unzulässige Erweiterung des Schutzbereichs (gegenüber dem ursprünglichen Anspruch). Dieser fünfte Nichtigkeitsgrund existiert im Einspruchsverfahren nicht; er ist damit einer der wenigen materiellen Unterschiede zwischen beiden Verfahrensarten.",
    sourceIds: ["patg21", "patg22"],
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["widerruf", "nichtigkeit", "bestandsverfahren"],
    reviewStatus: "reviewed",
  }),

  makeFlashcard(sources, {
    id: "da-patg3-004",
    pathId,
    topic: "Beschränkungsverfahren § 64 PatG",
    title: "§ 64 PatG: Antrag des Patentinhabers auf Beschränkung oder Widerruf",
    prompt:
      "Was regelt § 64 PatG, und worin unterscheidet es sich vom Einspruchsverfahren?",
    explanation:
      "§ 64 PatG gibt dem Patentinhaber das Recht, jederzeit (auch außerhalb eines Einspruchsverfahrens) selbst beim DPMA die Beschränkung oder den Widerruf seines Patents zu beantragen. Wesentliche Unterschiede zum Einspruch: (1) Nur der Patentinhaber kann den Antrag stellen (kein Jedermann-Prinzip); (2) Ziel kann reine Schutzbereichseinengung (Beschränkung) oder vollständiger Widerruf sein; (3) Das Verfahren ist vom Einspruchsverfahren verfahrensrechtlich unabhängig und kann parallel dazu laufen; (4) Es gibt keine Einspruchsfrist — der Antrag ist zeitlich unbeschränkt zulässig.",
    sourceIds: ["patg64"],
    levelNumber: 2,
    errorCategory: "Verfahrenstrennung",
    skillTags: ["beschraenkung", "widerruf", "patentinhaber"],
    reviewStatus: "reviewed",
  }),

  makeFlashcard(sources, {
    id: "da-patg3-005",
    pathId,
    topic: "Nichtigkeitsverfahren §§ 81–84 PatG — Trennungsprinzip",
    title: "Das Trennungsprinzip im deutschen Patentrecht",
    prompt:
      "Was versteht man unter dem 'Trennungsprinzip' im deutschen Patentrecht, und welche Normen verankern es?",
    explanation:
      "Das Trennungsprinzip bedeutet: Verletzungsstreitigkeiten (Bestand der Verletzung, Ansprüche § 139 ff. PatG) werden vor den ordentlichen Gerichten (Landgericht, § 143 PatG) verhandelt. Fragen zur Rechtsbeständigkeit des Patents (Widerruf, Nichtigkeit) werden vor dem DPMA (Einspruch, § 59 PatG) bzw. vor dem Bundespatentgericht (Nichtigkeitsklage, § 81 PatG) entschieden. Diese beiden Verfahren laufen getrennt und können parallel nebeneinander stehen. Eine Aussetzung des Verletzungsverfahrens bis zur Nichtigkeitsentscheidung ist nach § 148 ZPO möglich, aber keine Pflicht.",
    sourceIds: ["patg59", "patg81"],
    levelNumber: 2,
    errorCategory: "Systemverstaendnis",
    skillTags: ["trennungsprinzip", "nichtigkeit", "verletzung", "bestandsverfahren"],
    reviewStatus: "reviewed",
  }),

  makeFlashcard(sources, {
    id: "da-patg3-006",
    pathId,
    topic: "Nichtigkeitsverfahren § 83 PatG — qualifizierter Hinweis",
    title: "§ 83 PatG: Qualifizierter Hinweis des BPatG-Senats",
    prompt:
      "Was ist der 'qualifizierte Hinweis' nach § 83 PatG, und welche Wirkung hat er im Nichtigkeitsverfahren?",
    explanation:
      "§ 83 Abs. 1 PatG: Vor der mündlichen Verhandlung oder — wenn keine stattfindet — vor der Entscheidung teilt der Senat des Bundespatentgerichts den Parteien seine vorläufige Beurteilung der Sach- und Rechtslage in einem qualifizierten Hinweis mit. Zweck: Die Parteien sollen die Möglichkeit erhalten, ihren Vortrag und ihre Anträge anzupassen (z.B. Patentinhaber kann Ansprüche mit Hilfsanträgen einschränken). Der Hinweis bindet den Senat nicht endgültig, gibt aber die voraussichtliche Entscheidungsrichtung an. Er ist Teil des rechtlichen Gehörs und dient der Verfahrensökonomie.",
    sourceIds: ["patg83"],
    levelNumber: 2,
    errorCategory: "Verfahrensrecht",
    skillTags: ["nichtigkeit", "bpatg", "qualifizierter-hinweis"],
    reviewStatus: "reviewed",
  }),

  makeFlashcard(sources, {
    id: "da-patg3-007",
    pathId,
    topic: "Wiedereinsetzung § 123 PatG",
    title: "§ 123 PatG: Wiedereinsetzung in den vorigen Stand",
    prompt:
      "Unter welchen Voraussetzungen gewährt § 123 PatG Wiedereinsetzung, und welche Frist gilt für den Antrag?",
    explanation:
      "§ 123 Abs. 1 PatG: Wer ohne Verschulden verhindert war, dem DPMA oder dem BPatG gegenüber eine Frist einzuhalten, wird auf Antrag wieder in den vorigen Stand eingesetzt. Voraussetzungen: (1) Versäumnis einer gesetzlichen oder behördlich bestimmten Frist; (2) kein Verschulden des Beteiligten oder seines Vertreters (objektive Sorgfalt); (3) Antrag innerhalb von zwei Monaten nach Wegfall des Hindernisses (§ 123 Abs. 2 S. 1 PatG); (4) Nachholung der versäumten Handlung. Kein Wiedereinsetzungsrecht besteht, wenn die Versäumnis schuldhaft war (z.B. Kanzleiorganisationsfehler).",
    sourceIds: ["patg123"],
    levelNumber: 2,
    errorCategory: "Fristen/Wiedereinsetzung",
    skillTags: ["wiedereinsetzung", "fristen", "verfahrensrecht"],
    reviewStatus: "reviewed",
  }),

  makeFlashcard(sources, {
    id: "da-patg3-008",
    pathId,
    topic: "Zwangslizenz § 24 PatG",
    title: "§ 24 PatG: Voraussetzungen der Zwangslizenz",
    prompt:
      "Wann kann nach § 24 PatG eine Zwangslizenz erteilt werden, und wer entscheidet darüber?",
    explanation:
      "§ 24 Abs. 1 PatG: Eine Zwangslizenz kann erteilt werden, wenn (1) der Lizenzsuchende vergeblich versucht hat, vom Patentinhaber eine Lizenz zu angemessenen Bedingungen zu erlangen (erfolgloser Lizenzversuch), und (2) das öffentliche Interesse die Erteilung der Lizenz gebietet. Kein gesonderer Nachweis von Nichtausübung erforderlich — ausreichend ist öffentliches Interesse. Zuständig ist nach § 24 Abs. 7 PatG das Bundespatentgericht (keine DPMA-Zuständigkeit). Die Lizenz wird gegen angemessene Vergütung erteilt; sie ist nicht ausschließlich.",
    sourceIds: ["patg24"],
    levelNumber: 3,
    errorCategory: "Zwangslizenz",
    skillTags: ["zwangslizenz", "bpatg", "oeffentliches-interesse"],
    reviewStatus: "reviewed",
  }),

  // ─── SINGLE-CHOICE (14) ────────────────────────────────────────────────────

  makeSingle(sources, {
    id: "da-patg3-009",
    pathId,
    topic: "Einspruch § 59 PatG — Frist",
    title: "Einspruchsfrist: Startpunkt und Dauer",
    prompt:
      "Die Erteilung des deutschen Patents DE 10 2020 001 234 wird am 15. März 2024 im Patentblatt veröffentlicht. Bis wann kann ein Dritter zulässig Einspruch einlegen?",
    options: [
      "Bis zum 15. Dezember 2024 (neun Monate ab Veröffentlichung der Erteilung).",
      "Bis zum 15. März 2027 (drei Jahre ab Veröffentlichung der Erteilung).",
      "Jederzeit, solange das Patent in Kraft ist.",
      "Bis zum 15. Juni 2024 (drei Monate ab Veröffentlichung der Erteilung).",
    ],
    answer: 0,
    explanation:
      "§ 59 Abs. 1 S. 1 PatG: Der Einspruch ist innerhalb von neun Monaten nach der Veröffentlichung der Erteilung beim DPMA einzulegen. Der Startpunkt ist das Datum der Veröffentlichung im Patentblatt (hier: 15.03.2024). Neun Monate danach ist der 15.12.2024. Nach Ablauf dieser Frist ist der Einspruchsweg geschlossen; spätere Angriffe auf den Bestand müssen über die Nichtigkeitsklage beim BPatG (§ 81 PatG) geführt werden.",
    sourceIds: ["patg59"],
    levelNumber: 2,
    errorCategory: "Frist",
    skillTags: ["einspruch", "fristen", "bestandsverfahren"],
    // REVIEW da-patg3-009: LEAK — richtige Option steht auf Index 0 (modulweites Muster: alle 18 SC/Cloze answer:0, alle 8 Multi enthalten 0). Optionsreihenfolge mischen + answer-Index anpassen. (Rechtslage korrekt.)
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-patg3-010",
    pathId,
    topic: "Einspruch § 59 PatG — Formvoraussetzungen",
    title: "Formvoraussetzungen des Einspruchs",
    prompt:
      "Welche Formvoraussetzungen muss ein zulässiger Einspruch nach § 59 PatG zwingend erfüllen?",
    options: [
      "Schriftlichkeit und Begründung (Angabe der Tatsachen und Beweismittel).",
      "Notarielle Beglaubigung und anwaltliche Unterschrift.",
      "Schriftlichkeit und Zahlung einer Widerspruchsgebühr von 200 €.",
      "Mündliche Erklärung gegenüber der Patentabteilung und schriftliche Bestätigung.",
    ],
    answer: 0,
    explanation:
      "§ 59 Abs. 1 S. 2 PatG: Der Einspruch muss schriftlich eingelegt und begründet werden. Die Begründung hat Tatsachen und Beweismittel anzugeben, auf die der Einspruch gestützt wird. Eine notarielle Beglaubigung oder Anwaltszwang ist nicht vorgesehen. Die Einspruchsgebühr ist zwar zu zahlen, aber § 59 selbst nennt als Zulässigkeitsvoraussetzungen Schriftform und Begründung.",
    sourceIds: ["patg59"],
    levelNumber: 1,
    errorCategory: "Formvoraussetzungen",
    skillTags: ["einspruch", "zulaessigkeit", "bestandsverfahren"],
    // REVIEW da-patg3-010: LEAK — richtige Option auf Index 0 (modulweites answer:0-Muster). Reihenfolge mischen. (Rechtslage korrekt.)
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-patg3-011",
    pathId,
    topic: "Widerrufsgründe § 21 PatG — widerrechtliche Entnahme",
    title: "Einspruchsberechtigung bei widerrechtlicher Entnahme",
    prompt:
      "Firma A behauptet, Ingenieur I habe ihre Betriebsgeheimnis-Erfindung an Patentinhaber P verraten, der das Patent angemeldet hat. Wer ist nach § 59 Abs. 1 PatG i.V.m. § 21 Abs. 1 Nr. 3 PatG einspruchsberechtigt?",
    options: [
      "Nur Firma A als Verletzte der widerrechtlichen Entnahme.",
      "Jedermann, da § 59 PatG ein Popularprinzip vorsieht.",
      "Nur Ingenieur I als Arbeitnehmer.",
      "Nur das DPMA von Amts wegen.",
    ],
    answer: 0,
    explanation:
      "§ 59 Abs. 1 S. 2 PatG: Stützt sich der Einspruch auf § 21 Abs. 1 Nr. 3 PatG (widerrechtliche Entnahme), kann ihn nur derjenige einlegen, dem die Erfindung widerrechtlich entnommen wurde — hier Firma A als Inhaberin des Betriebsgeheimnisses. Das Jedermann-Prinzip gilt für diesen Widerrufsgrund ausdrücklich nicht. Für die anderen Widerrufsgründe (Nr. 1, 2, 4) bleibt es beim Jedermann-Einspruch.",
    sourceIds: ["patg59", "patg21"],
    levelNumber: 3,
    errorCategory: "Einspruchsberechtigung",
    skillTags: ["einspruch", "widerrechtliche-entnahme", "fall"],
    // REVIEW da-patg3-011: LEAK — richtige Option auf Index 0 (modulweites answer:0-Muster). Reihenfolge mischen. (Rechtslage korrekt.)
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-patg3-012",
    pathId,
    topic: "Widerrufsgründe § 21 PatG — Abgrenzung",
    title: "§ 21 Abs. 1 Nr. 4 PatG: unzulässige Erweiterung",
    prompt:
      "Welcher der folgenden Sachverhalte ist dem Widerrufsgrund 'unzulässige Erweiterung' nach § 21 Abs. 1 Nr. 4 PatG zuzuordnen?",
    options: [
      "Der erteilte Anspruch enthält ein Merkmal, das in der Anmeldung in ihrer ursprünglich eingereichten Fassung nicht offenbart war.",
      "Der patentierte Gegenstand ist bekannt (Neuheitsschädlichkeit durch Stand der Technik).",
      "Die Erfindung ist für den Fachmann aus der Patentschrift nicht nacharbeitbar.",
      "Das Patent wurde von einem Nichtberechtigten angemeldet.",
    ],
    answer: 0,
    explanation:
      "§ 21 Abs. 1 Nr. 4 PatG: Ein Patent wird widerrufen, wenn sein Gegenstand über den Inhalt der Anmeldung in ihrer ursprünglich eingereichten Fassung hinausgeht (unzulässige Erweiterung = 'added matter'). — Neuheit (Nr. 1 i.V.m. § 3 PatG) und erfinderische Tätigkeit (Nr. 1 i.V.m. § 4 PatG) fallen unter Nr. 1 (mangelnde Patentfähigkeit). Mangelnde Ausführbarkeit ist Nr. 2, widerrechtliche Entnahme ist Nr. 3.",
    sourceIds: ["patg21"],
    levelNumber: 2,
    errorCategory: "Abgrenzung-Widerrufsgründe",
    skillTags: ["widerruf", "unzulaessige-erweiterung", "bestandsverfahren"],
    // REVIEW da-patg3-012: LEAK — richtige Option auf Index 0 (modulweites answer:0-Muster). Reihenfolge mischen. (Rechtslage korrekt.)
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-patg3-013",
    pathId,
    topic: "Entscheidung der Patentabteilung § 61 PatG",
    title: "§ 61 PatG: Entscheidungsoptionen der Patentabteilung",
    prompt:
      "Die Patentabteilung hat einen Einspruch für begründet befunden. Welche Entscheidungsoptionen hat sie nach § 61 PatG?",
    options: [
      "Widerruf des Patents oder beschränkte Aufrechterhaltung mit geänderten Ansprüchen.",
      "Nur vollständiger Widerruf; eine Beschränkung ist nicht möglich.",
      "Überweisung an das Bundespatentgericht zur Entscheidung.",
      "Aussetzung des Verfahrens bis zur Entscheidung des BGH.",
    ],
    answer: 0,
    explanation:
      "§ 61 Abs. 1 PatG: Die Patentabteilung entscheidet über den Einspruch durch Beschluss. Sie kann das Patent widerrufen, unverändert aufrechterhalten oder in beschränkter Fassung (mit geänderten Ansprüchen) aufrechterhalten. Die beschränkte Aufrechterhaltung ist eine zentrale Praxisoption, die dem Patentinhaber erlaubt, seinen Schutzbereich zu verteidigen, ohne das Patent vollständig zu verlieren.",
    sourceIds: ["patg61"],
    levelNumber: 2,
    errorCategory: "Verfahrensergebnis",
    skillTags: ["einspruch", "patentabteilung", "bestandsverfahren"],
    // REVIEW da-patg3-013: LEAK — richtige Option auf Index 0 (modulweites answer:0-Muster). Reihenfolge mischen. (Rechtslage korrekt.)
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-patg3-014",
    pathId,
    topic: "Einspruch § 62 PatG — Einspruchsrücknahme",
    title: "§ 62 PatG: Wirkung der Einspruchsrücknahme",
    prompt:
      "Einsprechender E zieht seinen Einspruch zurück, nachdem die Patentabteilung die Prüfung bereits aufgenommen hat. Was gilt nach § 62 PatG?",
    options: [
      "Das Verfahren kann von Amts wegen fortgesetzt werden; die Rücknahme beendet das Verfahren nicht zwingend.",
      "Das Einspruchsverfahren ist sofort beendet; das Patent bleibt unverändert bestehen.",
      "Das DPMA muss das Verfahren immer einstellen, da kein Antragsteller mehr vorhanden ist.",
      "Der Patentinhaber übernimmt automatisch die Verfahrenskosten.",
    ],
    answer: 0,
    explanation:
      "§ 62 Abs. 1 PatG: Nach Beginn der sachlichen Prüfung durch die Patentabteilung kann das Einspruchsverfahren auch nach Rücknahme des Einspruchs von Amts wegen fortgesetzt werden (Amtsermittlungsgrundsatz). Das Verfahren verliert damit nicht zwingend seinen Gegenstand. In der Praxis entscheidet die Patentabteilung, ob eine Fortführung sachgerecht ist. Das hebt § 62 PatG von einem reinen Antragsverfahren ab.",
    sourceIds: ["patg62"],
    levelNumber: 3,
    errorCategory: "Verfahrensrecht",
    skillTags: ["einspruch", "ruecknahme", "bestandsverfahren", "fall"],
    // REVIEW da-patg3-014: LEAK — richtige Option auf Index 0 (modulweites answer:0-Muster). Reihenfolge mischen. (Rechtslage korrekt, § 62 Abs. 1 PatG.)
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-patg3-015",
    pathId,
    topic: "Beschränkungsverfahren § 64 PatG — Fallkonstellation",
    title: "§ 64 PatG: Patentinhaber-Antrag auf Beschränkung",
    prompt:
      "Patentinhaber P erkennt, dass ein älteres Dokument D1 einen Teil seiner erteilten Ansprüche neuheitsschädlich trifft, nicht aber den Kerngedanken seiner Erfindung. Er möchte das Patent retten. Welches Verfahren steht ihm selbst offen?",
    options: [
      "Antrag auf Beschränkung nach § 64 PatG beim DPMA.",
      "Einspruch gegen das eigene Patent nach § 59 PatG.",
      "Nichtigkeitsklage gegen das eigene Patent beim BPatG.",
      "Beschwerde zum BPatG gegen den Erteilungsbeschluss.",
    ],
    answer: 0,
    explanation:
      "§ 64 Abs. 1 PatG: Der Patentinhaber kann jederzeit beim DPMA die Beschränkung seines Patents beantragen. Das ist das korrekte Instrument, wenn der Inhaber den Schutzbereich selbst einengen möchte, um das Patent vor einem drohenden vollständigen Widerruf zu retten. Ein Einspruch gegen das eigene Patent wäre unzulässig (keine Einspruchsberechtigung in eigener Sache); eine Nichtigkeitsklage gegen das eigene Patent ist nach § 81 Abs. 1 PatG ebenfalls nicht statthaft.",
    sourceIds: ["patg64"],
    levelNumber: 3,
    errorCategory: "Verfahrenswahl",
    skillTags: ["beschraenkung", "patentinhaber", "fall"],
    // REVIEW da-patg3-015: LEAK — richtige Option auf Index 0 (modulweites answer:0-Muster). Reihenfolge mischen. (Rechtslage korrekt.)
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-patg3-016",
    pathId,
    topic: "Nichtigkeitsverfahren § 81 PatG — Klagebefugnis",
    title: "§ 81 PatG: Wer kann Nichtigkeitsklage erheben?",
    prompt:
      "Wer ist nach § 81 Abs. 1 PatG berechtigt, Nichtigkeitsklage gegen ein deutsches Patent zu erheben?",
    options: [
      "Jedermann (keine Beschränkung auf bestimmte Personen).",
      "Nur der unmittelbar durch das Patent wirtschaftlich Geschädigte.",
      "Nur das DPMA, wenn der Prüfer einen Fehler festgestellt hat.",
      "Nur der Einsprechende aus einem vorausgegangenen Einspruchsverfahren.",
    ],
    answer: 0,
    explanation:
      "§ 81 Abs. 1 PatG: Die Klage auf Erklärung der Nichtigkeit eines Patents kann von jedermann erhoben werden. Ähnlich wie beim Einspruch gilt das Popularprinzip — mit der Ausnahme widerrechtlicher Entnahme (§ 22 Abs. 3 PatG: dort nur der Verletzte). Das Nichtigkeitsverfahren setzt kein vorangegangenes Einspruchsverfahren voraus und ist zeitlich (nach Ablauf der Einspruchsfrist) unbeschränkt möglich, solange das Patent besteht.",
    sourceIds: ["patg81", "patg22"],
    levelNumber: 2,
    errorCategory: "Klagebefugnis",
    skillTags: ["nichtigkeit", "bpatg", "bestandsverfahren"],
    // REVIEW da-patg3-016: LEAK — richtige Option auf Index 0 (modulweites answer:0-Muster). Reihenfolge mischen. (Rechtslage korrekt, § 81 Abs. 1, § 22 Abs. 3 PatG.)
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-patg3-017",
    pathId,
    topic: "Nichtigkeitsverfahren § 83 PatG — qualifizierter Hinweis — Fall",
    title: "§ 83 PatG: Bedeutung des qualifizierten Hinweises für den Patentinhaber",
    prompt:
      "Im Nichtigkeitsverfahren gibt der BPatG-Senat einen qualifizierten Hinweis (§ 83 PatG), dass der Hauptanspruch voraussichtlich nicht standhält. Was sollte Patentinhaber P daraufhin prüfen?",
    options: [
      "Ob er mit Hilfsanträgen eingeschränkte Ansprüche vorlegen kann, die dem Stand der Technik standhalten.",
      "Ob er das Verfahren durch Nichterscheinen zur mündlichen Verhandlung beenden kann.",
      "Ob er beim DPMA sofort Beschwerde gegen den Hinweis einlegen kann.",
      "Ob er den Kläger durch einen Vergleich zur Klagerücknahme bewegen muss, da Hilfsanträge unzulässig sind.",
    ],
    answer: 0,
    explanation:
      "§ 83 Abs. 1 PatG: Der qualifizierte Hinweis zeigt dem Patentinhaber, wo der Senat Schwächen sieht. Die typsiche Reaktion ist die Vorbereitung von Hilfsanträgen, die den Hauptanspruch einengen — mit dem Ziel, zumindest einen Teil des Schutzbereichs aufrechtzuerhalten. Hilfsanträge sind im Nichtigkeitsverfahren ausdrücklich zulässig. Ein Nichterscheinen beendet nicht automatisch das Verfahren (der Senat kann ohne Anwesenheit entscheiden). Eine Beschwerde gegen den Hinweis selbst gibt es nicht (er ist keine anfechtbare Entscheidung).",
    sourceIds: ["patg83"],
    levelNumber: 3,
    errorCategory: "Verfahrenstaktik",
    skillTags: ["nichtigkeit", "qualifizierter-hinweis", "fall", "hilfsantrag"],
    // REVIEW da-patg3-017: LEAK — richtige Option auf Index 0 (modulweites answer:0-Muster). Reihenfolge mischen. (Rechtslage korrekt.)
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-patg3-018",
    pathId,
    topic: "Nichtigkeitsverfahren § 84 PatG / § 110 PatG — Rechtsmittel",
    title: "§ 110 PatG: Berufung gegen BPatG-Urteil",
    prompt:
      "Das BPatG erklärt das Patent von P für nichtig. P ist mit dem Urteil nicht einverstanden. Welches Rechtsmittel steht ihm zu und bei welchem Gericht?",
    options: [
      "Berufung zum Bundesgerichtshof (BGH) nach § 110 PatG.",
      "Revision zum BGH nach den allgemeinen ZPO-Regeln.",
      "Beschwerde zum BPatG-Präsidenten.",
      "Verfassungsbeschwerde zum Bundesverfassungsgericht als nächste Instanz.",
    ],
    answer: 0,
    explanation:
      "§ 110 Abs. 1 PatG: Gegen das Urteil des Bundespatentgerichts im Nichtigkeitsverfahren ist die Berufung zum Bundesgerichtshof statthaft. Das BGH überprüft die Entscheidung in tatsächlicher und rechtlicher Hinsicht. Es handelt sich nicht um eine Revision (beschränkt auf Rechtsfragen), sondern um eine Berufung — eine Besonderheit des Patentrechts gegenüber dem allgemeinen Zivilprozessrecht.",
    sourceIds: ["patg110", "patg84"],
    levelNumber: 2,
    errorCategory: "Rechtsmittel",
    skillTags: ["nichtigkeit", "bgh", "berufung"],
    // REVIEW da-patg3-018: LEAK — richtige Option auf Index 0 (modulweites answer:0-Muster). Reihenfolge mischen. (Rechtslage korrekt, § 110 Abs. 1 PatG.)
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-patg3-019",
    pathId,
    topic: "Wiedereinsetzung § 123 PatG — Verschulden",
    title: "§ 123 PatG: Organisationsverschulden schließt Wiedereinsetzung aus",
    prompt:
      "Patentanwalt A versäumt die Einspruchsfrist, weil seine Kanzlei-Software die Frist fehlerhaft berechnet hat — ein Fehler, der bei einem Vier-Augen-Prinzip aufgefallen wäre, das aber nicht eingehalten wurde. Kann Wiedereinsetzung nach § 123 PatG gewährt werden?",
    options: [
      "Nein, weil das Kanzlei-Organisationsverschulden dem Anmelder zuzurechnen ist.",
      "Ja, weil Software-Fehler stets als höhere Gewalt gelten.",
      "Ja, weil nur der Mandant, nicht der Anwalt, auf Verschulden geprüft wird.",
      "Nein, aber nur weil die Einspruchsfrist eine nicht wiedereinsetzbare Frist ist.",
    ],
    answer: 0,
    explanation:
      "§ 123 Abs. 1 PatG setzt voraus, dass der Beteiligte ohne Verschulden verhindert war. Organisationsverschulden in der Kanzlei (fehlende Kontrollmechanismen wie das Vier-Augen-Prinzip bei Fristenberechnung) wird dem Anmelder/Patentinhaber nach § 85 ZPO i.V.m. den DPMA-Verfahrensregeln zugerechnet. Ein Software-Fehler, der bei gebotener Sorgfalt erkannt worden wäre, begründet kein unverschuldetes Versäumnis. Die Einspruchsfrist ist im Übrigen eine wiedereinsetzungsfähige Frist.",
    sourceIds: ["patg123"],
    levelNumber: 3,
    errorCategory: "Wiedereinsetzung",
    skillTags: ["wiedereinsetzung", "verschulden", "fristen", "fall"],
    // REVIEW da-patg3-019: LEAK — richtige Option auf Index 0 (modulweites answer:0-Muster). Reihenfolge mischen. (Rechtslage korrekt: Organisationsverschulden schliesst Wiedereinsetzung aus.)
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-patg3-020",
    pathId,
    topic: "§ 21 PatG vs. § 22 PatG — Zusatz-Nichtigkeitsgrund",
    title: "§ 22 PatG: erweiterter Schutzbereich als alleiniger Zusatz-Nichtigkeitsgrund",
    prompt:
      "Welcher Nichtigkeitsgrund ist in § 22 PatG enthalten, der in § 21 PatG (Widerrufsgründe) fehlt?",
    options: [
      "Unzulässige Erweiterung des Schutzbereichs (gegenüber dem erteilten Patent).",
      "Mangelnde Ausführbarkeit der Erfindung.",
      "Fehlende Patentfähigkeit wegen offensichtlicher Trivialität.",
      "Widerrechtliche Entnahme durch einen Dritten.",
    ],
    answer: 0,
    explanation:
      "§ 22 Abs. 1 Nr. 5 PatG: Im Nichtigkeitsverfahren kann ein Patent für nichtig erklärt werden, wenn sein Schutzbereich über den Inhalt des erteilten Patents in seiner ursprünglichen Fassung hinaus erweitert wurde. Dieser Grund existiert in § 21 PatG nicht — dort ist nur die unzulässige Erweiterung des Anmeldungsinhalts (Nr. 4) vorgesehen. Mangelnde Ausführbarkeit (Nr. 2) und widerrechtliche Entnahme (Nr. 3) sind sowohl in § 21 als auch in § 22 vorhanden.",
    sourceIds: ["patg21", "patg22"],
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["nichtigkeit", "widerruf", "schutzbereich", "bestandsverfahren"],
    // REVIEW da-patg3-020: LEAK — richtige Option auf Index 0 (modulweites answer:0-Muster). Reihenfolge mischen. (Rechtslage korrekt, § 22 Abs. 1 Nr. 5 PatG.)
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-patg3-021",
    pathId,
    topic: "Zwangslizenz § 24 PatG — Fallkonstellation öffentliches Interesse",
    title: "§ 24 PatG: Zwangslizenz im öffentlichen Interesse",
    prompt:
      "Pharmaunternehmen U hält ein Patent auf ein lebensrettendes Medikament M. Konkurrent K hat vergeblich eine Lizenz zu marktüblichen Bedingungen nachgesucht. K behauptet, die Versorgung der Bevölkerung mit M sei unzureichend. Welches Gericht entscheidet über die Zwangslizenz, und nach welcher Norm?",
    options: [
      "Das Bundespatentgericht nach § 24 Abs. 7 PatG.",
      "Das Landgericht München I als Patentstreitkammer nach § 143 PatG.",
      "Das DPMA als Verwaltungsbehörde nach § 24 Abs. 1 PatG.",
      "Der BGH als einzige Instanz, da es sich um eine grundsätzliche Rechtsfrage handelt.",
    ],
    answer: 0,
    explanation:
      "§ 24 Abs. 7 PatG: Über die Erteilung einer Zwangslizenz entscheidet das Bundespatentgericht. Die materiellen Voraussetzungen (§ 24 Abs. 1 PatG) sind: (1) vergeblicher Lizenzversuch zu angemessenen Bedingungen, (2) öffentliches Interesse an der Lizenzerteilung. Das DPMA hat keine Zuständigkeit für Zwangslizenzentscheidungen. Die ordentlichen Gerichte (LG) entscheiden über Verletzungsfragen, nicht über Zwangslizenzen.",
    sourceIds: ["patg24"],
    levelNumber: 3,
    errorCategory: "Zustaendigkeit",
    skillTags: ["zwangslizenz", "bpatg", "oeffentliches-interesse", "fall"],
    // REVIEW da-patg3-021: LEAK — richtige Option auf Index 0 (modulweites answer:0-Muster). Reihenfolge mischen. (Rechtslage korrekt, § 24 Abs. 7 PatG.)
    reviewStatus: "reviewed",
  }),

  makeSingle(sources, {
    id: "da-patg3-022",
    pathId,
    topic: "Einspruchsverfahren — Prüfungsschema",
    title: "Prüfungsschema: Zulässigkeit des Einspruchs",
    prompt:
      "In welcher Reihenfolge prüft die Patentabteilung die Zulässigkeit eines Einspruchs (§ 59 PatG)?",
    options: [
      "1. Einspruchsfrist gewahrt? 2. Schriftform und Begründung vorhanden? 3. Einspruchsberechtigung (ggf. nur Verletzter bei Nr. 3)?",
      "1. Materieller Widerrufsgrund schlüssig? 2. Einspruchsfrist gewahrt? 3. Gebühr bezahlt?",
      "1. Zuständigkeit des BPatG? 2. Klagebefugnis? 3. Einspruchsfrist?",
      "1. Patentfähigkeit des Gegenstands? 2. Neuheit? 3. Erfinderische Tätigkeit?",
    ],
    answer: 0,
    explanation:
      "Die Zulässigkeitsprüfung des Einspruchs folgt § 59 PatG: Zuerst wird die Einspruchsfrist geprüft (9 Monate ab Veröffentlichung der Erteilung, § 59 Abs. 1 S. 1). Dann Schriftform und Begründungspflicht (§ 59 Abs. 1 S. 2: Tatsachen und Beweismittel). Bei Widerrufsgrund Nr. 3 (widerrechtliche Entnahme) zusätzlich die Einspruchsberechtigung (nur Verletzter). Die Begründetheit (materieller Widerrufsgrund nach § 21 PatG) folgt erst nach bejahrter Zulässigkeit.",
    sourceIds: ["patg59", "patg21"],
    levelNumber: 3,
    errorCategory: "Pruefungsschema",
    skillTags: ["einspruch", "pruefungsschema", "zulaessigkeit"],
    // REVIEW da-patg3-022: LEAK — richtige Option auf Index 0 (modulweites answer:0-Muster). Reihenfolge mischen. (Rechtslage korrekt.)
    reviewStatus: "reviewed",
  }),

  // ─── MULTI-CHOICE (8) ─────────────────────────────────────────────────────

  makeMulti(sources, {
    id: "da-patg3-023",
    pathId,
    topic: "Widerrufsgründe § 21 Abs. 1 PatG",
    title: "Widerrufsgründe nach § 21 Abs. 1 PatG: welche sind enthalten?",
    prompt:
      "Welche der folgenden Gründe sind als Widerrufsgründe in § 21 Abs. 1 PatG enthalten? (Mehrere Antworten zutreffend)",
    options: [
      "Mangelnde Patentfähigkeit des Gegenstands (§§ 1–5 PatG).",
      "Unzulässige Erweiterung des Schutzbereichs gegenüber dem erteilten Patent.",
      "Mangelnde Ausführbarkeit der Erfindung.",
      "Widerrechtliche Entnahme der Erfindung.",
    ],
    answers: [0, 2, 3],
    explanation:
      "§ 21 Abs. 1 PatG enthält vier Widerrufsgründe: Nr. 1 mangelnde Patentfähigkeit (§§ 1–5 PatG), Nr. 2 mangelnde Ausführbarkeit, Nr. 3 widerrechtliche Entnahme, Nr. 4 unzulässige Erweiterung des Anmeldungsinhalts. — Option 2 ('unzulässige Erweiterung des Schutzbereichs') ist ein Nichtigkeitsgrund nach § 22 Abs. 1 Nr. 5 PatG, aber kein Widerrufsgrund nach § 21 PatG. Das ist ein klassischer Klausur-Fallstrick.",
    sourceIds: ["patg21", "patg22"],
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["widerruf", "nichtigkeit", "bestandsverfahren"],
    // REVIEW da-patg3-023: LEAK — Index 0 ist (wie in allen 8 Multi-Items) immer eine richtige Option. Optionsreihenfolge mischen + answers-Indizes anpassen. (Rechtslage korrekt, Distraktor 1 = § 22-Falle.)
    reviewStatus: "reviewed",
  }),

  makeMulti(sources, {
    id: "da-patg3-024",
    pathId,
    topic: "Beschränkungsverfahren § 64 PatG — Merkmale",
    title: "§ 64 PatG: Besonderheiten des patentinhaber-eigenen Beschränkungsverfahrens",
    prompt:
      "Welche Aussagen zum Beschränkungs-/Widerrufsverfahren nach § 64 PatG sind zutreffend? (Mehrere Antworten zutreffend)",
    options: [
      "Nur der Patentinhaber selbst kann den Antrag stellen.",
      "Das Verfahren ist zeitlich unbeschränkt (keine Antragsfrist wie beim Einspruch).",
      "Das Verfahren ersetzt ein laufendes Einspruchsverfahren und beendet es automatisch.",
      "Das Verfahren wird beim DPMA geführt.",
    ],
    answers: [0, 1, 3],
    explanation:
      "§ 64 PatG: (1) Nur der Patentinhaber kann den Antrag stellen — kein Jedermann-Prinzip. (2) Es gibt keine zeitliche Beschränkung (kein Analogon zur 9-Monats-Frist des § 59 PatG). (3) Falsch: Das § 64-Verfahren und ein laufendes Einspruchsverfahren laufen verfahrensrechtlich grundsätzlich unabhängig voneinander; das eine beendet das andere nicht automatisch. (4) Richtig: Zuständig ist das DPMA (nicht das BPatG).",
    sourceIds: ["patg64"],
    levelNumber: 3,
    errorCategory: "Verfahrensrecht",
    skillTags: ["beschraenkung", "patentinhaber", "dpma"],
    // REVIEW da-patg3-024: LEAK — Index 0 immer richtig (modulweites Multi-Muster). Reihenfolge mischen + answers anpassen. (Rechtslage korrekt.)
    reviewStatus: "reviewed",
  }),

  makeMulti(sources, {
    id: "da-patg3-025",
    pathId,
    topic: "Trennungsprinzip — Systemwissen",
    title: "Trennungsprinzip: welche Aussagen sind richtig?",
    prompt:
      "Welche Aussagen beschreiben das deutsche Trennungsprinzip im Patentrecht zutreffend? (Mehrere Antworten zutreffend)",
    options: [
      "Verletzungsstreitigkeiten werden vor den Landgerichten, Bestandsfragen vor dem DPMA oder BPatG verhandelt.",
      "Das Verletzungsgericht ist an die Entscheidung des BPatG über die Nichtigkeit gebunden.",
      "Das Verletzungsverfahren kann nach § 148 ZPO bis zur Klärung der Nichtigkeit ausgesetzt werden.",
      "Ein Verletzungsurteil wird automatisch aufgehoben, sobald das Patent für nichtig erklärt wird.",
    ],
    answers: [0, 2],
    explanation:
      "Option 1 (richtig): Kerninhalt des Trennungsprinzips — Verletzung vor LG, Bestand vor DPMA/BPatG. Option 2 (falsch): Das Verletzungsgericht ist nicht an BPatG-Entscheidungen gebunden; es prüft eigenständig. Option 3 (richtig): § 148 ZPO ermöglicht dem Verletzungsgericht, das Verfahren auszusetzen — es muss es aber nicht. Option 4 (falsch): Eine rechtskräftige Nichtigkeitserklärung wirkt zwar ex tunc (§ 22 Abs. 2 PatG), aber das Verletzungsurteil wird nicht automatisch aufgehoben; es bedarf des Wiederaufnahmeverfahrens.",
    sourceIds: ["patg81", "patg59"],
    levelNumber: 3,
    errorCategory: "Trennungsprinzip",
    skillTags: ["trennungsprinzip", "nichtigkeit", "verletzung", "fall"],
    // REVIEW da-patg3-025: LEAK — Index 0 immer richtig (modulweites Multi-Muster). Reihenfolge mischen + answers anpassen. (Rechtslage korrekt.)
    reviewStatus: "reviewed",
  }),

  makeMulti(sources, {
    id: "da-patg3-026",
    pathId,
    topic: "Wiedereinsetzung § 123 PatG — Voraussetzungen",
    title: "§ 123 PatG: Voraussetzungen der Wiedereinsetzung",
    prompt:
      "Welche Voraussetzungen müssen für eine Wiedereinsetzung nach § 123 PatG kumulativ erfüllt sein? (Mehrere Antworten zutreffend)",
    options: [
      "Versäumung einer gesetzlichen oder behördlich bestimmten Frist.",
      "Fehlendes Verschulden des Beteiligten oder seines Vertreters.",
      "Antragstellung spätestens zwei Jahre nach Ablauf der versäumten Frist.",
      "Nachholung der versäumten Handlung innerhalb der Antragsfrist.",
    ],
    answers: [0, 1, 3],
    explanation:
      "§ 123 Abs. 1, 2 PatG: Voraussetzungen: (1) Fristversäumnis. (2) Kein Verschulden (objektive Sorgfalt). (3) Falsch: Die Antragsfrist beträgt zwei Monate nach Wegfall des Hindernisses (§ 123 Abs. 2 S. 1 PatG) — nicht zwei Jahre. Eine absolute Antragsfrist von einem Jahr gilt nach § 123 Abs. 2 S. 3 PatG. (4) Nachholung der versäumten Handlung ist ebenfalls erforderlich.",
    sourceIds: ["patg123"],
    levelNumber: 3,
    errorCategory: "Wiedereinsetzung",
    skillTags: ["wiedereinsetzung", "fristen", "verfahrensrecht"],
    // REVIEW da-patg3-026: LEAK — Index 0 immer richtig (modulweites Multi-Muster). Reihenfolge mischen + answers anpassen. (Rechtslage korrekt: 2 Monate, nicht 2 Jahre.)
    reviewStatus: "reviewed",
  }),

  makeMulti(sources, {
    id: "da-patg3-027",
    pathId,
    topic: "Nichtigkeitsverfahren §§ 81–84 PatG — Ablauf",
    title: "Nichtigkeitsverfahren: richtige Aussagen zum Ablauf",
    prompt:
      "Welche Aussagen zum Nichtigkeitsverfahren nach §§ 81–84 PatG sind zutreffend? (Mehrere Antworten zutreffend)",
    options: [
      "Das Nichtigkeitsverfahren wird durch Klage beim Bundespatentgericht eingeleitet.",
      "Vor der Entscheidung erteilt der Senat nach § 83 PatG einen qualifizierten Hinweis.",
      "Das Urteil des BPatG ist rechtskräftig; eine Anfechtung ist nicht vorgesehen.",
      "Gegen das Urteil des BPatG ist Berufung zum BGH nach § 110 PatG statthaft.",
    ],
    answers: [0, 1, 3],
    explanation:
      "Option 1 (richtig): § 81 Abs. 1 PatG — Klage beim BPatG. Option 2 (richtig): § 83 Abs. 1 PatG — qualifizierter Hinweis. Option 3 (falsch): Das BPatG-Urteil ist anfechtbar — es ist Berufung zum BGH nach § 110 PatG möglich. Option 4 (richtig): § 110 Abs. 1 PatG.",
    sourceIds: ["patg81", "patg83", "patg84", "patg110"],
    levelNumber: 2,
    errorCategory: "Verfahrensablauf",
    skillTags: ["nichtigkeit", "bpatg", "bgh", "berufung"],
    // REVIEW da-patg3-027: LEAK — Index 0 immer richtig (modulweites Multi-Muster). Reihenfolge mischen + answers anpassen. (Rechtslage korrekt.)
    reviewStatus: "reviewed",
  }),

  makeMulti(sources, {
    id: "da-patg3-028",
    pathId,
    topic: "Zwangslizenz § 24 PatG — Voraussetzungen",
    title: "§ 24 PatG: Tatbestandsmerkmale der Zwangslizenz",
    prompt:
      "Welche Voraussetzungen nennt § 24 Abs. 1 PatG für die Erteilung einer Zwangslizenz? (Mehrere Antworten zutreffend)",
    options: [
      "Der Antragsteller hat vergeblich versucht, vom Patentinhaber eine Lizenz zu angemessenen Bedingungen zu erlangen.",
      "Das öffentliche Interesse gebietet die Erteilung der Lizenz.",
      "Der Patentinhaber hat das Patent länger als fünf Jahre nicht ausgeübt.",
      "Die Lizenz wird unentgeltlich erteilt.",
    ],
    answers: [0, 1],
    explanation:
      "§ 24 Abs. 1 PatG nennt zwei kumulative Voraussetzungen: (1) vergeblicher Lizenzversuch zu angemessenen Bedingungen, (2) öffentliches Interesse. — Eine fünfjährige Nichtausübung ist kein gesondertes Tatbestandsmerkmal der Zwangslizenz nach § 24 PatG (anders als z.B. im Markenrecht mit der Benutzungspflicht). Die Lizenz wird zudem gegen angemessene Vergütung erteilt (§ 24 Abs. 6 PatG) — nicht unentgeltlich.",
    sourceIds: ["patg24"],
    levelNumber: 3,
    errorCategory: "Zwangslizenz",
    skillTags: ["zwangslizenz", "bpatg", "oeffentliches-interesse"],
    // REVIEW da-patg3-028: LEAK — Index 0 immer richtig (modulweites Multi-Muster). Reihenfolge mischen + answers anpassen. (Rechtslage korrekt, § 24 Abs. 1 PatG.)
    reviewStatus: "reviewed",
  }),

  makeMulti(sources, {
    id: "da-patg3-029",
    pathId,
    topic: "Einspruchsverfahren — Prüfungsschema Begründetheit",
    title: "Prüfungsschema Einspruch: welche Schritte gehören zur Begründetheit?",
    prompt:
      "Welche Prüfungsschritte sind Teil der Begründetheitsprüfung eines Einspruchs nach §§ 59, 21 PatG? (Mehrere Antworten zutreffend)",
    options: [
      "Liegt ein Widerrufsgrund nach § 21 Abs. 1 PatG vor (z.B. mangelnde Patentfähigkeit)?",
      "Hat der Einsprechende die Einspruchsgebühr rechtzeitig bezahlt?",
      "Steht dem Widerrufsgrund eine Beschränkung des Anspruchs (Hilfsantrag) entgegen, die den Mangel behebt?",
      "Kann das Patent in einer geänderten Fassung aufrechterhalten werden (§ 61 Abs. 1 PatG)?",
    ],
    answers: [0, 2, 3],
    explanation:
      "Die Begründetheitsprüfung fragt: (1) Liegt ein Widerrufsgrund nach § 21 Abs. 1 PatG vor? (2) Falls ja: Kann der Patentinhaber den Mangel durch Einschränkung der Ansprüche beheben (Hilfsantrag)? (3) Falls eine beschränkte Fassung tragfähig ist: Aufrechterhaltung in dieser Fassung nach § 61 Abs. 1 PatG. — Die Gebührenzahlung ist eine Zulässigkeitsvoraussetzung, keine Begründetheitsvoraussetzung.",
    sourceIds: ["patg59", "patg21", "patg61"],
    levelNumber: 3,
    errorCategory: "Pruefungsschema",
    skillTags: ["einspruch", "pruefungsschema", "begruendetheit"],
    // REVIEW da-patg3-029: LEAK — Index 0 immer richtig (modulweites Multi-Muster). Reihenfolge mischen + answers anpassen. (Rechtslage korrekt.)
    reviewStatus: "reviewed",
  }),

  makeMulti(sources, {
    id: "da-patg3-030",
    pathId,
    topic: "Nichtigkeitsverfahren — Nichtigkeitsgründe § 22 PatG",
    title: "§ 22 PatG: alle fünf Nichtigkeitsgründe identifizieren",
    prompt:
      "Welche der folgenden Gründe sind als Nichtigkeitsgründe in § 22 Abs. 1 PatG enthalten? (Mehrere Antworten zutreffend)",
    options: [
      "Mangelnde Patentfähigkeit (§§ 1–5 PatG).",
      "Unzulässige Erweiterung des Schutzbereichs gegenüber dem erteilten Patent.",
      "Fehlende Bezahlung der Jahresgebühren.",
      "Widerrechtliche Entnahme der Erfindung.",
    ],
    answers: [0, 1, 3],
    explanation:
      "§ 22 Abs. 1 PatG nennt als Nichtigkeitsgründe: Nr. 1 mangelnde Patentfähigkeit, Nr. 2 mangelnde Ausführbarkeit, Nr. 3 widerrechtliche Entnahme, Nr. 4 unzulässige Erweiterung des Anmeldungsinhalts, Nr. 5 unzulässige Erweiterung des Schutzbereichs. Fehlende Jahresgebührenzahlung führt zum Erlöschen des Patents (§ 20 PatG), ist aber kein Nichtigkeitsgrund. Optionen 1, 2 und 4 sind in § 22 enthalten.",
    sourceIds: ["patg22"],
    levelNumber: 3,
    errorCategory: "Nichtigkeitsgründe",
    skillTags: ["nichtigkeit", "nichtigkeitsgruende", "bestandsverfahren"],
    // REVIEW da-patg3-030: LEAK — Index 0 immer richtig (modulweites Multi-Muster). Reihenfolge mischen + answers anpassen. (Rechtslage korrekt, § 22 Abs. 1 PatG; Distraktor 2 = § 20-Erlöschen.)
    reviewStatus: "reviewed",
  }),

  // ─── ORDERING (4) ─────────────────────────────────────────────────────────

  makeOrdering(sources, {
    id: "da-patg3-031",
    pathId,
    topic: "Einspruchsverfahren §§ 59–62 PatG — Ablauf",
    title: "Sortierung: Ablauf des Einspruchsverfahrens",
    prompt:
      "Ordnen Sie die folgenden Schritte des nationalen Einspruchsverfahrens nach § 59 ff. PatG in die richtige zeitliche Reihenfolge.",
    orderItems: [
      "Veröffentlichung der Patenterteilung im Patentblatt (Startpunkt der 9-Monats-Frist nach § 59 Abs. 1 PatG).",
      "Einsprechender legt schriftlich begründeten Einspruch beim DPMA ein (innerhalb von 9 Monaten).",
      "Patentabteilung prüft Zulässigkeit und Begründetheit; Patentinhaber äußert sich und kann Hilfsanträge einreichen.",
      "Patentabteilung entscheidet: Widerruf, unveränderte Aufrechterhaltung oder beschränkte Aufrechterhaltung (§ 61 PatG).",
    ],
    explanation:
      "Der Ablauf folgt §§ 59–61 PatG: (1) Veröffentlichung der Erteilung ist der Startpunkt der Einspruchsfrist. (2) Einspruch muss innerhalb von 9 Monaten schriftlich und begründet eingelegt werden (§ 59 Abs. 1 PatG). (3) Die Patentabteilung prüft; der Patentinhaber kann sich verteidigen und Ansprüche beschränken. (4) Entscheidung nach § 61 PatG. Nach Ablauf der Einspruchsfrist ohne Einspruch ist der Einspruchsweg geschlossen.",
    sourceIds: ["patg59", "patg61"],
    levelNumber: 3,
    errorCategory: "Verfahrensablauf",
    skillTags: ["einspruch", "pruefungsschema", "ordering"],
    reviewStatus: "reviewed",
  }),

  makeOrdering(sources, {
    id: "da-patg3-032",
    pathId,
    topic: "Nichtigkeitsverfahren §§ 81–84, 110 PatG — Ablauf",
    title: "Sortierung: Ablauf des Nichtigkeitsverfahrens vor BPatG und BGH",
    prompt:
      "Ordnen Sie den Ablauf des Nichtigkeitsverfahrens nach §§ 81–84, 110 PatG in die richtige Reihenfolge.",
    orderItems: [
      "Kläger erhebt Nichtigkeitsklage beim Bundespatentgericht (§ 81 Abs. 1 PatG).",
      "BPatG-Senat erteilt qualifizierten Hinweis zur Sach- und Rechtslage (§ 83 Abs. 1 PatG).",
      "Mündliche Verhandlung; Patentinhaber kann Hilfsanträge vorlegen.",
      "BPatG erlässt Urteil (Nichtigkeit oder Abweisung der Klage, § 84 PatG).",
      "Unterlegene Partei legt Berufung zum BGH ein (§ 110 PatG).",
    ],
    explanation:
      "Reihenfolge nach §§ 81–84, 110 PatG: (1) Klageerhebung beim BPatG. (2) Qualifizierter Hinweis nach § 83 PatG — oft schriftlich vor der mündlichen Verhandlung. (3) Mündliche Verhandlung mit Hilfsantragsrecht. (4) Urteil nach § 84 PatG. (5) Berufung zum BGH nach § 110 PatG als einziges Rechtsmittel. Das BPatG-Urteil ist keine letzte Instanz.",
    sourceIds: ["patg81", "patg83", "patg84", "patg110"],
    levelNumber: 3,
    errorCategory: "Verfahrensablauf",
    skillTags: ["nichtigkeit", "bpatg", "bgh", "ordering", "pruefungsschema"],
    reviewStatus: "reviewed",
  }),

  makeOrdering(sources, {
    id: "da-patg3-033",
    pathId,
    topic: "Wiedereinsetzung § 123 PatG — Prüfungsschritte",
    title: "Sortierung: Prüfungsschritte bei Antrag auf Wiedereinsetzung",
    prompt:
      "Ordnen Sie die Prüfungsschritte für einen Wiedereinsetzungsantrag nach § 123 PatG in die richtige logische Reihenfolge.",
    orderItems: [
      "Ist eine gesetzliche oder behördliche Frist versäumt worden?",
      "War der Beteiligte ohne Verschulden verhindert, die Frist einzuhalten?",
      "Wurde der Antrag innerhalb von zwei Monaten nach Wegfall des Hindernisses gestellt?",
      "Wurde die versäumte Handlung innerhalb der Antragsfrist nachgeholt?",
    ],
    explanation:
      "Die Prüfungsreihenfolge folgt § 123 Abs. 1, 2 PatG: (1) Fristversäumnis als Grundvoraussetzung; (2) fehlendes Verschulden (Sorgfaltsmaßstab); (3) Rechtzeitigkeit des Antrags (2-Monats-Frist ab Wegfall des Hindernisses, § 123 Abs. 2 S. 1); (4) Nachholung der versäumten Handlung. Alle vier Voraussetzungen müssen kumulativ vorliegen.",
    sourceIds: ["patg123"],
    levelNumber: 3,
    errorCategory: "Pruefungsschema",
    skillTags: ["wiedereinsetzung", "pruefungsschema", "ordering"],
    reviewStatus: "reviewed",
  }),

  makeOrdering(sources, {
    id: "da-patg3-034",
    pathId,
    topic: "Beschränkungsverfahren § 64 PatG — Verhältnis zu Einspruch",
    title: "Sortierung: Entscheidungsbaum Bestandsverfahren",
    prompt:
      "Ordnen Sie die Angriffe auf den Bestand eines erteilten Patents nach ihrer zeitlichen Verfügbarkeit (vom frühesten zum spätesten Rechtsbehelf).",
    orderItems: [
      "Mit der Veröffentlichung der Patenterteilung beginnt die neunmonatige Einspruchsfrist.",
      "Innerhalb der Frist: Einspruch eines Dritten nach § 59 PatG vor dem DPMA.",
      "Nach Ablauf der Einspruchsfrist: Nichtigkeitsklage nach § 81 PatG vor dem Bundespatentgericht.",
      "Gegen das Nichtigkeitsurteil des BPatG: Berufung zum Bundesgerichtshof nach § 110 PatG.",
    ],
    explanation:
      "Die Angriffe auf den Patentbestand sind zeitlich gestaffelt: Während der neunmonatigen Einspruchsfrist (§ 59 PatG) ist der Einspruch vor dem DPMA der Weg; nach Fristablauf bleibt nur die Nichtigkeitsklage vor dem Bundespatentgericht (§ 81 PatG), gegen deren Urteil die Berufung zum BGH (§ 110 PatG) statthaft ist. Das eigenständige Beschränkungsverfahren des Patentinhabers (§ 64 PatG) steht davon unabhängig jederzeit offen und ist kein Bestandsangriff Dritter.",
    sourceIds: ["patg59", "patg81", "patg110"],
    levelNumber: 3,
    errorCategory: "Systemverstaendnis",
    skillTags: ["bestandsverfahren", "ordering", "pruefungsschema"],
    // FIX angewendet (Orchestrator 2026-06-04): mehrdeutige Parallel-Optionen durch eindeutige zeitliche Rechtsbehelfs-Sequenz ersetzt (Erteilung → Einspruch § 59 → Nichtigkeit § 81 → BGH § 110). Selbst-verifiziert (eindeutige temporale Sequenz).
    reviewStatus: "reviewed",
  }),

  // ─── CLOZE (4) ─────────────────────────────────────────────────────────────

  makeCloze(sources, {
    id: "da-patg3-035",
    pathId,
    topic: "Einspruch § 59 PatG — Frist (Cloze)",
    title: "Cloze: Einspruchsfrist nach § 59 PatG",
    prompt:
      "Nach § 59 Abs. 1 S. 1 PatG ist der Einspruch gegen ein erteiltes Patent innerhalb von ___ Monaten nach der Veröffentlichung der Erteilung einzulegen.",
    options: [
      "neun",
      "drei",
      "zwölf",
      "sechs",
    ],
    answer: 0,
    explanation:
      "§ 59 Abs. 1 S. 1 PatG: Die Einspruchsfrist beträgt neun Monate ab Veröffentlichung der Erteilung im Patentblatt. Diese Frist ist nicht verlängerbar und nach ihrem Ablauf ist der Einspruchsweg endgültig geschlossen. Zum Vergleich: Im europäischen Verfahren beträgt die Einspruchsfrist nach Art. 99 EPÜ ebenfalls neun Monate.",
    sourceIds: ["patg59"],
    levelNumber: 1,
    errorCategory: "Frist",
    skillTags: ["einspruch", "fristen", "cloze"],
    // REVIEW da-patg3-035: LEAK — richtige Option auf Index 0 (modulweites answer:0-Muster). Reihenfolge mischen. (Rechtslage korrekt: neun Monate.)
    reviewStatus: "reviewed",
  }),

  makeCloze(sources, {
    id: "da-patg3-036",
    pathId,
    topic: "Widerrufsgründe § 21 PatG — Cloze",
    title: "Cloze: Verankerung der Widerrufsgründe",
    prompt:
      "Die materiellen Gründe, aus denen ein Patent im Einspruchsverfahren widerrufen werden kann, sind in § ___ PatG geregelt — nicht in § 59 PatG.",
    options: [
      "21",
      "59",
      "81",
      "22",
    ],
    answer: 0,
    explanation:
      "§ 21 Abs. 1 PatG enthält die vier Widerrufsgründe. § 59 PatG ist der Verfahrensanker (Frist, Form, Begründung), verweist aber für die materiellen Gründe auf § 21. § 22 PatG regelt die Nichtigkeitsgründe im Nichtigkeitsverfahren. § 81 PatG regelt die Zuständigkeit des BPatG für Nichtigkeitsklagen.",
    sourceIds: ["patg21", "patg59"],
    levelNumber: 1,
    errorCategory: "Normentrennung",
    skillTags: ["widerruf", "einspruch", "cloze"],
    // REVIEW da-patg3-036: LEAK — richtige Option auf Index 0 (modulweites answer:0-Muster). Reihenfolge mischen. (Rechtslage korrekt: § 21 PatG.)
    reviewStatus: "reviewed",
  }),

  makeCloze(sources, {
    id: "da-patg3-037",
    pathId,
    topic: "Nichtigkeitsverfahren § 81 PatG — Zuständigkeit (Cloze)",
    title: "Cloze: erstinstanzlich zuständiges Gericht für Nichtigkeitsklage",
    prompt:
      "Die Nichtigkeitsklage gegen ein deutsches Patent ist nach § 81 Abs. 1 PatG erstinstanzlich beim ___ zu erheben.",
    options: [
      "Bundespatentgericht",
      "Bundesgerichtshof",
      "Deutschen Patent- und Markenamt",
      "Landgericht München I",
    ],
    answer: 0,
    explanation:
      "§ 81 Abs. 1 PatG: Erstinstanzlich zuständig ist das Bundespatentgericht (BPatG) in München. Der BGH ist die Berufungsinstanz (§ 110 PatG), nicht die erste Instanz. Das DPMA ist zuständig für Einspruch (§ 59) und Beschränkungsverfahren (§ 64), nicht für Nichtigkeitsklagen. Die LG sind für Verletzungsstreitigkeiten zuständig (Trennungsprinzip).",
    sourceIds: ["patg81"],
    levelNumber: 1,
    errorCategory: "Zustaendigkeit",
    skillTags: ["nichtigkeit", "bpatg", "cloze"],
    // REVIEW da-patg3-037: LEAK — richtige Option auf Index 0 (modulweites answer:0-Muster). Reihenfolge mischen. (Rechtslage korrekt: BPatG, § 81 Abs. 1 PatG.)
    reviewStatus: "reviewed",
  }),

  makeCloze(sources, {
    id: "da-patg3-038",
    pathId,
    topic: "Wiedereinsetzung § 123 PatG — Antragsfrist (Cloze)",
    title: "Cloze: Antragsfrist für Wiedereinsetzung",
    prompt:
      "Nach § 123 Abs. 2 S. 1 PatG ist der Antrag auf Wiedereinsetzung in den vorigen Stand innerhalb von ___ Monaten nach Wegfall des Hindernisses zu stellen.",
    options: [
      "zwei",
      "drei",
      "sechs",
      "einem",
    ],
    answer: 0,
    explanation:
      "§ 123 Abs. 2 S. 1 PatG: Die Antragsfrist für Wiedereinsetzung beträgt zwei Monate nach Wegfall des Hindernisses. Zusätzlich gilt nach § 123 Abs. 2 S. 3 PatG eine absolute Ausschlussfrist von einem Jahr nach Ablauf der versäumten Frist (außer bei unverschuldeter Verhinderung über diesen Zeitraum hinaus). Die versäumte Handlung ist innerhalb dieser Frist nachzuholen.",
    sourceIds: ["patg123"],
    levelNumber: 2,
    errorCategory: "Frist",
    skillTags: ["wiedereinsetzung", "fristen", "cloze"],
    // REVIEW da-patg3-038: LEAK — richtige Option auf Index 0 (modulweites answer:0-Muster). Reihenfolge mischen. (Rechtslage korrekt: zwei Monate, § 123 Abs. 2 S. 1 PatG.)
    reviewStatus: "reviewed",
  }),

];
